import { useEffect, useState, type FormEvent } from 'react';
import type {
  ApiKeyIssueResult,
  ApiUser,
  CreateApiUserInput,
  UpdateApiUserInput,
} from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { CheckboxField, TextField } from '../../components/ui/Field.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { Modal } from '../../components/ui/Modal.js';
import { RolePicker } from '../roles/RolePicker.js';
import { useCreateApiUser, useUpdateApiUser } from './api.js';

interface ApiUserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** When provided the modal edits this API user; otherwise it creates a new one. */
  apiUser?: ApiUser | null;
}

interface FormState {
  name: string;
  expiresAt: string;
  roleIds: string[];
  isActive: boolean;
}

const EMPTY_FORM: FormState = { name: '', expiresAt: '', roleIds: [], isActive: true };

/** yyyy-mm-dd for the date input, from an ISO timestamp. */
function toDateInput(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

function toFormState(apiUser: ApiUser): FormState {
  return {
    name: apiUser.name,
    expiresAt: toDateInput(apiUser.expiresAt),
    roleIds: apiUser.roleIds,
    isActive: apiUser.isActive,
  };
}

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

export function ApiUserFormModal({
  isOpen,
  onClose,
  apiUser = null,
}: ApiUserFormModalProps): JSX.Element {
  const isEdit = apiUser !== null;
  const saveMode = useSaveMode();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [issued, setIssued] = useState<ApiKeyIssueResult | null>(null);
  const [copied, setCopied] = useState(false);

  const createMutation = useCreateApiUser();
  const updateMutation = useUpdateApiUser();
  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetSaveMode = saveMode.reset;

  useEffect(() => {
    if (isOpen) {
      setForm(apiUser ? toFormState(apiUser) : EMPTY_FORM);
      setIssued(null);
      setCopied(false);
      resetSaveMode();
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, apiUser, resetSaveMode, resetCreate, resetUpdate]);

  const activeMutation = isEdit ? updateMutation : createMutation;
  const fieldErrors = fieldErrorsOf(activeMutation.error);
  const generalError =
    activeMutation.error instanceof ApiError &&
    !activeMutation.error.envelope?.error.details?.length
      ? activeMutation.error.message
      : activeMutation.isError && !(activeMutation.error instanceof ApiError)
        ? `Failed to ${isEdit ? 'update' : 'create'} API user.`
        : null;

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (isEdit && apiUser) {
      const input: UpdateApiUserInput = {
        name: form.name.trim(),
        roleIds: form.roleIds,
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
        isActive: form.isActive,
      };
      updateMutation.mutate(
        { id: apiUser.id, input },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
      return;
    }
    const payload: CreateApiUserInput = {
      name: form.name.trim(),
      roleIds: form.roleIds,
      expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
    };
    createMutation.mutate(payload, {
      onSuccess: (result) => setIssued(result),
    });
  };

  const handleCopy = (): void => {
    if (!issued) {
      return;
    }
    void navigator.clipboard.writeText(issued.plaintextKey).then(() => {
      setCopied(true);
    });
  };

  if (issued) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="API key created"
        footer={<Button onClick={onClose}>Done</Button>}
      >
        <div className="flex flex-col gap-4">
          <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Copy this key now. For security it is shown only once and cannot be retrieved again.
          </div>
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
              {issued.name}
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-sm text-slate-800">
                {issued.plaintextKey}
              </code>
              <Button variant="secondary" onClick={handleCopy}>
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit API user' : 'New API user'}
      footer={
        <FormActions
          formId="api-user-form"
          saveMode={saveMode}
          isPending={activeMutation.isPending}
          onCancel={onClose}
        />
      }
    >
      <form id="api-user-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {generalError ? (
          <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {generalError}
          </div>
        ) : null}

        <TextField
          label="Name"
          required
          value={form.name}
          maxLength={200}
          autoFocus
          placeholder="e.g. CI pipeline"
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          error={fieldErrors.name}
        />
        <TextField
          label="Expires at"
          type="date"
          value={form.expiresAt}
          onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
          error={fieldErrors.expiresAt}
        />
        <p className="-mt-2 text-xs text-slate-500">Leave blank for a key that never expires.</p>

        {isEdit ? (
          <div>
            <CheckboxField
              label="Active"
              checked={form.isActive}
              onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
            />
            <p className="mt-1 text-xs text-slate-500">
              Uncheck to disable this key without deleting it. Revoked keys cannot be reactivated.
            </p>
          </div>
        ) : null}

        <div>
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">
            Access (RBAC roles)
          </p>
          <p className="mb-2 text-xs text-slate-500">
            An API user&apos;s permissions are the combined access of its assigned roles. Manage
            what each role can see and do on the Roles page.
          </p>
          <RolePicker
            selectedIds={form.roleIds}
            onChange={(roleIds) => setForm((f) => ({ ...f, roleIds }))}
          />
        </div>
      </form>
    </Modal>
  );
}
